/**
 * Queue Monitor Component
 * Displays CVE sync queue statistics and real-time monitoring
 */

import { useState, useEffect } from 'react';
import { getApiUrl } from '../config/api';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  QueueListIcon,
} from '@heroicons/react/24/outline';

interface QueueStats {
  pending_sync: {
    total_pending: number;
    never_synced: number;
    needs_refresh: number;
  };
  recent_activity: {
    versions_synced: number;
    successful_syncs: number;
    failed_syncs: number;
    avg_duration_ms: number;
  };
  recent_jobs: Array<{
    id: number;
    job_type: string;
    status: string;
    scheduled_at: string;
    started_at: string | null;
    completed_at: string | null;
    duration_ms: number | null;
    error_message: string | null;
    result: string | null;
    software_name: string | null;
  }>;
  sync_status_breakdown: Array<{
    sync_status: string;
    count: number;
  }>;
  sync_activity: Array<{
    date: string;
    syncs_completed: number;
    successful: number;
    failed: number;
  }>;
  queue_config: {
    max_batch_size: number;
    max_concurrency: number;
    max_batch_timeout: number;
    max_retries: number;
  };
  processing_estimates: {
    avg_duration_ms: number;
    estimated_rate_per_hour: number;
    estimated_hours_to_clear: number;
  };
}

export function QueueMonitor() {
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enqueuing, setEnqueuing] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const loadStats = async () => {
    try {
      setError(null);
      const res = await fetchWithAuth(getApiUrl('api/v1/admin/queue-stats'));
      const data = await res.json();
      if (data.success) {
        setStats(data.data);
      } else {
        setError(data.error || 'Failed to load queue stats');
      }
    } catch (err) {
      console.error('Error loading queue stats:', err);
      setError('Failed to load queue stats');
    } finally {
      setLoading(false);
    }
  };

  const handleEnqueueAll = async () => {
    if (!confirm('This will enqueue all pending OS versions for CVE sync. Continue?')) {
      return;
    }

    setEnqueuing(true);
    try {
      const res = await fetchWithAuth(getApiUrl('api/v1/admin/enqueue-os-cve-sync'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`HTTP ${res.status}: ${errorText}`);
      }

      const data = await res.json();

      if (data.success) {
        alert(`Success! Enqueued ${data.versionsEnqueued} OS versions for CVE sync.`);
        loadStats(); // Refresh stats
      } else {
        alert(`Error: ${data.error || 'Unknown error'}\n${data.details || ''}`);
      }
    } catch (err) {
      console.error('Error enqueuing CVE sync:', err);
      alert(`Failed to enqueue CVE sync: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setEnqueuing(false);
    }
  };

  const handleCleanupStaleJobs = async () => {
    setCleaning(true);
    try {
      const res = await fetchWithAuth(getApiUrl('api/v1/admin/cleanup-stale-jobs'), {
        method: 'POST',
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message);
        loadStats();
      } else {
        alert(`Error: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Failed to cleanup stale jobs: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setCleaning(false);
    }
  };

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  if (loading && !stats) {
    return (
      <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-6">
        <div className="text-center py-8">
          <XCircleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400">{error}</p>
          <button
            onClick={loadStats}
            className="mt-4 px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/30 rounded hover:bg-red-500/20 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-400';
      case 'failed':
        return 'text-red-400';
      case 'running':
        return 'text-blue-400';
      default:
        return 'text-gray-400';
    }
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const successRate = stats?.recent_activity.versions_synced
    ? Math.round((stats.recent_activity.successful_syncs / stats.recent_activity.versions_synced) * 100)
    : 0;

  return (
    <div className="bg-gray-800/30 border border-gray-700 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <QueueListIcon className="w-6 h-6 text-blue-400" />
          <h2 className="text-xl font-semibold text-gray-100">CVE Sync Queue Monitor</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCleanupStaleJobs}
            disabled={cleaning}
            className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 rounded hover:bg-yellow-500/20 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            title="Reset stuck 'running' jobs that never started to failed status"
          >
            {cleaning ? (
              <>
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                Cleaning...
              </>
            ) : (
              <>
                <XCircleIcon className="w-4 h-4" />
                Cleanup Stuck Jobs
              </>
            )}
          </button>
          <button
            onClick={handleEnqueueAll}
            disabled={enqueuing}
            className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 text-green-400 border border-green-500/30 rounded hover:bg-green-500/20 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {enqueuing ? (
              <>
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                Enqueuing...
              </>
            ) : (
              <>
                <ArrowPathIcon className="w-4 h-4" />
                Enqueue All
              </>
            )}
          </button>
          <button
            onClick={loadStats}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 text-blue-400 border border-blue-500/30 rounded hover:bg-blue-500/20 transition-colors text-sm"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {stats && (
        <div className="p-6 space-y-6">
          {/* Key Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Pending Versions */}
            <div className="bg-gray-900/30 border border-gray-700/50 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <ClockIcon className="w-5 h-5 text-orange-400" />
              </div>
              <div className="text-2xl font-bold text-orange-400">
                {stats.pending_sync.total_pending}
              </div>
              <div className="text-xs text-gray-400 mt-1">Pending Sync</div>
              <div className="text-xs text-orange-300 mt-2">
                {stats.pending_sync.never_synced} never synced
              </div>
            </div>

            {/* Synced Today */}
            <div className="bg-gray-900/30 border border-gray-700/50 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <CheckCircleIcon className="w-5 h-5 text-green-400" />
              </div>
              <div className="text-2xl font-bold text-green-400">
                {stats.recent_activity.versions_synced || 0}
              </div>
              <div className="text-xs text-gray-400 mt-1">Synced (24h)</div>
              <div className="text-xs text-green-300 mt-2">
                {successRate}% success rate
              </div>
            </div>

            {/* Processing Rate */}
            <div className="bg-gray-900/30 border border-gray-700/50 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <ArrowPathIcon className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-2xl font-bold text-blue-400">
                {stats.processing_estimates.estimated_rate_per_hour}
              </div>
              <div className="text-xs text-gray-400 mt-1">Versions/Hour</div>
              <div className="text-xs text-blue-300 mt-2">
                Avg: {formatDuration(stats.processing_estimates.avg_duration_ms)}
              </div>
            </div>

            {/* Time to Clear */}
            <div className="bg-gray-900/30 border border-gray-700/50 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <ClockIcon className="w-5 h-5 text-purple-400" />
              </div>
              <div className="text-2xl font-bold text-purple-400">
                {stats.processing_estimates.estimated_hours_to_clear.toFixed(1)}h
              </div>
              <div className="text-xs text-gray-400 mt-1">Est. to Clear</div>
              <div className="text-xs text-purple-300 mt-2">
                At current rate
              </div>
            </div>
          </div>

          {/* Queue Configuration */}
          <div className="bg-gray-900/30 border border-gray-700/50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-300 mb-3">Queue Configuration</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-gray-400">Batch Size</div>
                <div className="text-gray-100 font-semibold">
                  {stats.queue_config.max_batch_size}
                </div>
              </div>
              <div>
                <div className="text-gray-400">Concurrency</div>
                <div className="text-gray-100 font-semibold">
                  {stats.queue_config.max_concurrency}
                </div>
              </div>
              <div>
                <div className="text-gray-400">Batch Timeout</div>
                <div className="text-gray-100 font-semibold">
                  {stats.queue_config.max_batch_timeout}s
                </div>
              </div>
              <div>
                <div className="text-gray-400">Max Retries</div>
                <div className="text-gray-100 font-semibold">
                  {stats.queue_config.max_retries}
                </div>
              </div>
            </div>
          </div>

          {/* Sync Status Breakdown */}
          <div className="bg-gray-900/30 border border-gray-700/50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-300 mb-3">Sync Status Breakdown</h3>
            <div className="space-y-2">
              {stats.sync_status_breakdown.map((item) => (
                <div key={item.sync_status} className="flex items-center justify-between">
                  <span className="text-sm text-gray-400 capitalize">
                    {item.sync_status.replace(/_/g, ' ')}
                  </span>
                  <span className="text-sm font-semibold text-gray-100">
                    {item.count}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Jobs */}
          <div className="bg-gray-900/30 border border-gray-700/50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-300 mb-3">Recent Jobs</h3>
            {stats.recent_jobs.length > 0 ? (
              <div className="space-y-2">
                {stats.recent_jobs.map((job) => {
                  const timestamp = job.completed_at || job.started_at || job.scheduled_at;
                  const timeAgo = timestamp ? (() => {
                    const diff = Date.now() - new Date(timestamp + 'Z').getTime();
                    if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
                    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
                    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
                    return new Date(timestamp + 'Z').toLocaleDateString();
                  })() : '—';
                  const fullDate = timestamp
                    ? new Date(timestamp + 'Z').toLocaleString()
                    : 'Unknown';
                  let resultSummary = '';
                  if (job.result) {
                    try {
                      const r = JSON.parse(job.result);
                      const parts = [];
                      if (r.versions_found != null) parts.push(`${r.versions_found} versions`);
                      if (r.cves_found != null) parts.push(`${r.cves_found} CVEs`);
                      if (r.new_versions != null) parts.push(`${r.new_versions} new`);
                      resultSummary = parts.join(', ');
                    } catch { resultSummary = job.result.substring(0, 60); }
                  }
                  return (
                    <div
                      key={job.id}
                      className="py-2 border-b border-gray-700/30 last:border-0"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          {job.status === 'completed' ? (
                            <CheckCircleIcon className="w-4 h-4 text-green-400 shrink-0" />
                          ) : job.status === 'failed' ? (
                            <XCircleIcon className="w-4 h-4 text-red-400 shrink-0" />
                          ) : (
                            <ArrowPathIcon className="w-4 h-4 text-blue-400 animate-spin shrink-0" />
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-300">
                                #{job.id}
                              </span>
                              <span className="text-xs text-gray-500 bg-gray-700/50 px-1.5 py-0.5 rounded">
                                {job.job_type || 'job'}
                              </span>
                              {job.software_name && (
                                <span className="text-xs text-gray-400 truncate max-w-[140px]">
                                  {job.software_name}
                                </span>
                              )}
                            </div>
                            {resultSummary && (
                              <div className="text-xs text-gray-500 mt-0.5">{resultSummary}</div>
                            )}
                            {job.error_message && (
                              <div className="text-xs text-red-400 mt-0.5 truncate max-w-xs" title={job.error_message}>
                                {job.error_message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-3">
                          <div className={`text-xs font-medium ${getStatusColor(job.status)}`}>
                            {job.status}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5" title={fullDate}>
                            {timeAgo}
                          </div>
                          {job.duration_ms != null && (
                            <div className="text-xs text-gray-600">
                              {formatDuration(job.duration_ms)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No recent jobs</p>
            )}
          </div>

          {/* Sync Activity (Last 7 Days) */}
          {stats.sync_activity.length > 0 && (
            <div className="bg-gray-900/30 border border-gray-700/50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-300 mb-3">
                Sync Activity (Last 7 Days)
              </h3>
              <div className="space-y-2">
                {stats.sync_activity.map((item) => (
                  <div key={item.date} className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">
                      {new Date(item.date).toLocaleDateString()}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-green-400">
                        {item.successful} ✓
                      </span>
                      {item.failed > 0 && (
                        <span className="text-sm text-red-400">
                          {item.failed} ✗
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
